import type { Card, CardStatus, ChecklistItem, FilterState, KpiSnapshot } from "../data/types";

// ── Filter cards by active filter state ─────────────────────────

export function filterCards(cards: Card[], filters: FilterState): Card[] {
  const { owner, status, channel, week, search } = filters;
  return cards.filter((card) => {
    if (owner !== "all" && card.owner !== owner) return false;
    if (status !== "all" && card.status !== status) return false;
    if (
      channel !== "all" &&
      !card.tags.some((t) => t.toLowerCase().includes(channel.toLowerCase())) &&
      card.channel?.toLowerCase() !== channel.toLowerCase()
    )
      return false;
    if (week !== "all" && card.weekNumber !== undefined && card.weekNumber !== week) return false;
    if (
      search &&
      !card.title.toLowerCase().includes(search.toLowerCase()) &&
      !card.description.toLowerCase().includes(search.toLowerCase())
    )
      return false;
    return true;
  });
}

// ── Count cards by status ───────────────────────────────────────

export interface StatusCounts {
  planned: number;
  in_progress: number;
  blocked: number;
  done: number;
  total: number;
}

export function computeStatusCounts(cards: Card[]): StatusCounts {
  return {
    planned: cards.filter((c) => c.status === "planned").length,
    in_progress: cards.filter((c) => c.status === "in_progress").length,
    blocked: cards.filter((c) => c.status === "blocked").length,
    done: cards.filter((c) => c.status === "done").length,
    total: cards.length,
  };
}

// ── Aggregate KPI totals ────────────────────────────────────────

export interface KpiTotals {
  demosBooked: number;
  closedWon: number;
  newMrr: number;
  trialToPaid: number;
  activityCompletion: number;
}

export function computeKpiTotals(kpis: KpiSnapshot[]): KpiTotals {
  return {
    demosBooked: kpis.reduce((s, k) => s + k.demosBooked, 0),
    closedWon: kpis.reduce((s, k) => s + k.closedWon, 0),
    newMrr: kpis.reduce((s, k) => s + k.newMrrEur, 0),
    trialToPaid:
      kpis.length > 0
        ? Math.round(
            kpis.reduce((s, k) => s + k.trialToPaidPct, 0) /
              (kpis.filter((k) => k.trialToPaidPct > 0).length || 1),
          )
        : 0,
    activityCompletion:
      kpis.length > 0
        ? Math.round(
            kpis.reduce((s, k) => s + k.activityCompletionPct, 0) /
              (kpis.filter((k) => k.activityCompletionPct > 0).length || 1),
          )
        : 0,
  };
}

// ── Dependency warnings ─────────────────────────────────────────

export interface DependencyWarning {
  cardId: string;
  blockedBy: string;
}

export function computeDependencyWarnings(cards: Card[]): DependencyWarning[] {
  const cardMap = new Map(cards.map((c) => [c.id, c]));
  const warnings: DependencyWarning[] = [];
  for (const card of cards) {
    if (card.status === "in_progress" || card.status === "planned") {
      for (const depId of card.dependsOn) {
        const dep = cardMap.get(depId);
        if (dep && dep.status !== "done") {
          warnings.push({ cardId: card.id, blockedBy: depId });
        }
      }
    }
  }
  return warnings;
}

// ── Prune / Scale decision signal ───────────────────────────────

export type PruneScaleSignal = "scale" | "hold" | "prune";

export interface PruneScaleMetrics {
  sql: number;       // Sales-qualified leads
  demos: number;     // Demos booked
  trials: number;    // Trials started
  cash: number;      // Cash collected (€)
}

/**
 * Computes a channel prune/scale/hold signal based on conversion and cash metrics.
 *
 * Thresholds (configurable per phase):
 * - SCALE: cash > 2000 AND demos >= 5 AND trials >= 2
 * - PRUNE: cash === 0 AND demos < 2 AND trials === 0
 * - HOLD: everything in between
 */
export function computePruneScaleSignal(input: PruneScaleMetrics): PruneScaleSignal {
  // Scale condition: meaningful revenue traction
  if (input.cash > 2000 && input.demos >= 5 && input.trials >= 2) {
    return "scale";
  }

  // Prune condition: no traction at all
  if (input.cash === 0 && input.demos < 2 && input.trials === 0) {
    return "prune";
  }

  return "hold";
}

// ── Checklist progress ──────────────────────────────────────────

export interface ChecklistProgress {
  total: number;
  checked: number;
  pct: number;
}

export function computeChecklistProgress(items: ChecklistItem[]): ChecklistProgress {
  const total = items.length;
  const checked = items.filter((c) => c.checked).length;
  return { total, checked, pct: total > 0 ? Math.round((checked / total) * 100) : 0 };
}
